import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import LanguageToggle from "@/components/LanguageToggle";
import { useLocale } from "@/i18n/useLocale";

const Navbar = () => {
  const { t } = useTranslation();
  const { L } = useLocale();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  const TOP_LEVEL_LINKS = [
    { to: "/products", label: t("nav.products") },
    { to: "/science", label: t("nav.science") },
    { to: "/markets", label: t("nav.markets") },
    { to: "/about", label: t("nav.about") },
    { to: "/distributor-portal", label: t("nav.distributor") },
  ];

  const RESOURCES_LINKS = [
    { to: "/resources#brochures", label: t("resources_menu.brochures"), hint: t("resources_menu.brochures_hint") },
    { to: "/resources#usecases", label: t("resources_menu.use_cases"), hint: t("resources_menu.use_cases_hint") },
    { to: "/resources#factsheets", label: t("resources_menu.fact_sheets"), hint: t("resources_menu.fact_sheets_hint") },
    { to: "/journal", label: t("resources_menu.journal"), hint: t("resources_menu.journal_hint") },
    { to: "/blog", label: t("resources_menu.blogs"), hint: t("resources_menu.blogs_hint") },
    { to: "/certifications", label: t("resources_menu.certifications"), hint: t("resources_menu.certifications_hint") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setResourcesOpen(false); setMobileResourcesOpen(false); }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!resourcesOpen) return;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setResourcesOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setResourcesOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onEsc); };
  }, [resourcesOpen]);

  const isHomeRoot = location.pathname === "/" || /^\/(de|ja)\/?$/.test(location.pathname);
  const isHomeHero = isHomeRoot && !scrolled;
  const barClass = isHomeHero
    ? "bg-transparent text-brand-cream border-transparent"
    : "bg-brand-cream/95 backdrop-blur-md text-brand-black border-brand-green/10";

  const pathSansLocale = location.pathname.replace(/^\/(de|ja)(?=\/|$)/, "") || "/";
  const resourcesActive = ["/resources", "/journal", "/blog", "/certifications"].some((p) => pathSansLocale.startsWith(p));

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 border-b transition-all duration-300 ${barClass}`} data-testid="site-navbar">
        <div className="container-wide flex items-center justify-between h-[72px] gap-3">
          <Link to={L("/")} className="flex items-center shrink-0" data-testid="nav-logo">
            <span className="font-display text-2xl tracking-tight font-semibold">SPIRUVA<sup className="text-[0.42em] align-super ml-px font-medium tracking-normal">™</sup></span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-7">
            {TOP_LEVEL_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={L(l.to)}
                data-testid={`nav-link-${l.to.slice(1)}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide whitespace-nowrap transition-colors hover:opacity-100 ${isActive ? "opacity-100 font-medium text-brand-gold" : "opacity-75"}`
                }
              >
                {l.label}
              </NavLink>
            ))}

            {/* Resources dropdown */}
            <div className="relative" ref={dropdownRef} onMouseEnter={() => setResourcesOpen(true)} onMouseLeave={() => setResourcesOpen(false)}>
              <button
                type="button"
                onClick={() => setResourcesOpen((v) => !v)}
                aria-expanded={resourcesOpen}
                aria-haspopup="menu"
                data-testid="nav-resources-toggle"
                className={`flex items-center gap-1.5 text-sm tracking-wide whitespace-nowrap transition-colors ${resourcesActive ? "opacity-100 font-medium text-brand-gold" : "opacity-75 hover:opacity-100"}`}
              >
                {t("nav.resources")}
                <ChevronDown size={14} className={`transition-transform duration-200 ${resourcesOpen ? "rotate-180" : ""}`} />
              </button>
              <div
                role="menu"
                data-testid="nav-resources-dropdown"
                className={`absolute right-0 top-full pt-3 w-[340px] transition-all duration-200 ${resourcesOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"}`}
              >
                <div className="bg-brand-cream border border-brand-green/15 shadow-2xl">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-gold/90 px-5 pt-4 pb-2">◦  {t("nav.library")}</p>
                  <ul className="pb-2">
                    {RESOURCES_LINKS.map((r) => (
                      <li key={r.to}>
                        <Link
                          to={L(r.to)}
                          data-testid={`nav-resources-${r.to.split("#")[0].replace(/\//g, "").replace(/[^a-z]+/gi, '-').toLowerCase()}${r.to.includes("#") ? "-" + r.to.split("#")[1] : ""}`}
                          className="block px-5 py-3 hover:bg-brand-green/5 transition group"
                          role="menuitem"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-display text-base text-brand-black">{r.label}</span>
                            <ArrowUpRight size={14} className="text-brand-black/30 group-hover:text-brand-gold transition" />
                          </div>
                          <p className="text-[11px] text-brand-black/55 leading-snug mt-0.5">{r.hint}</p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <LanguageToggle variant="navbar" />

            <Link
              to={L("/reserve-allocation")}
              data-testid="nav-cta-reserve"
              className={`hidden xl:inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-medium transition-all duration-300 min-h-[44px] ${
                isHomeHero
                  ? "bg-brand-gold text-brand-black hover:bg-brand-cream"
                  : "bg-brand-green text-brand-cream hover:bg-brand-black"
              }`}
            >
              {t("nav.reserve_cta")}
            </Link>

            <button
              onClick={() => setOpen(!open)}
              className="xl:hidden p-2 -mr-2 w-11 h-11 flex items-center justify-center"
              aria-label={open ? t("nav.close_menu") : t("nav.open_menu")}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile slide-over drawer */}
      <div
        className={`xl:hidden fixed inset-0 z-[90] transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!open}
      >
        <div
          className={`absolute inset-0 bg-brand-black/70 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
          onClick={() => setOpen(false)}
          data-testid="mobile-menu-backdrop"
        />
        <aside
          className={`absolute top-0 right-0 h-full w-[88%] max-w-sm bg-brand-cream text-brand-black shadow-2xl transition-transform duration-300 ease-out flex flex-col overflow-hidden ${open ? "translate-x-0" : "translate-x-full"}`}
          data-testid="mobile-menu-overlay"
          role="dialog"
          aria-label="Mobile navigation"
        >
          <div className="flex items-center justify-between h-[72px] px-6 border-b border-brand-green/15 shrink-0">
            <span className="font-display text-xl tracking-tight font-semibold">SPIRUVA<sup className="text-[0.42em] align-super ml-px font-medium tracking-normal">™</sup></span>
            <button
              onClick={() => setOpen(false)}
              className="w-11 h-11 -mr-2 flex items-center justify-center"
              aria-label={t("nav.close_menu")}
              data-testid="mobile-menu-close"
            >
              <X size={22} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-6 py-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/45 mb-4">◦  {t("nav.navigate")}</p>
            <ul className="space-y-1">
              {TOP_LEVEL_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={L(l.to)}
                    data-testid={`mobile-nav-link-${l.to.slice(1)}`}
                    className="flex items-center justify-between font-display text-2xl py-3 min-h-[44px] border-b border-brand-green/10 hover:text-brand-gold transition group"
                  >
                    <span>{l.label}</span>
                    <ArrowUpRight size={18} className="text-brand-black/30 group-hover:text-brand-gold group-hover:translate-x-1 transition-transform" />
                  </Link>
                </li>
              ))}

              <li>
                <button
                  type="button"
                  onClick={() => setMobileResourcesOpen((v) => !v)}
                  aria-expanded={mobileResourcesOpen}
                  data-testid="mobile-nav-resources-toggle"
                  className="w-full flex items-center justify-between font-display text-2xl py-3 min-h-[44px] border-b border-brand-green/10 hover:text-brand-gold transition"
                >
                  <span>{t("nav.resources")}</span>
                  <ChevronDown size={20} className={`text-brand-black/40 transition-transform duration-200 ${mobileResourcesOpen ? "rotate-180" : ""}`} />
                </button>
                <div className={`overflow-hidden transition-[max-height] duration-300 ${mobileResourcesOpen ? "max-h-[600px]" : "max-h-0"}`}>
                  <ul className="pl-4 py-2 space-y-0.5 border-l-2 border-brand-gold/30 ml-1">
                    {RESOURCES_LINKS.map((r) => (
                      <li key={r.to}>
                        <Link
                          to={L(r.to)}
                          data-testid={`mobile-nav-resources-${r.to.split("#")[0].replace(/\//g, "").toLowerCase()}${r.to.includes("#") ? "-" + r.to.split("#")[1] : ""}`}
                          className="flex items-center justify-between font-display text-lg py-3 min-h-[44px] pl-3 hover:text-brand-gold transition group"
                        >
                          <span>{r.label}</span>
                          <ArrowUpRight size={14} className="text-brand-black/30 group-hover:text-brand-gold transition" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            </ul>

            <div className="mt-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/45 mb-4">◦  {t("nav.language")}</p>
              <LanguageToggle variant="mobile" />
            </div>
          </nav>

          <div className="px-6 py-5 border-t border-brand-green/15 bg-white space-y-3 shrink-0">
            <Link
              to={L("/reserve-allocation")}
              data-testid="mobile-cta-reserve"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black py-4 text-xs uppercase tracking-[0.22em] font-medium hover:bg-brand-green hover:text-brand-cream transition min-h-[48px]"
            >
              {t("nav.reserve_2027")} <ArrowUpRight size={14} />
            </Link>
            <Link
              to={L("/distributor-portal")}
              data-testid="mobile-cta-distributor"
              className="w-full inline-flex items-center justify-center gap-2 border border-brand-green/30 text-brand-black py-4 text-xs uppercase tracking-[0.22em] hover:bg-brand-green hover:text-brand-cream hover:border-brand-green transition min-h-[48px]"
            >
              {t("nav.distributor")}
            </Link>
          </div>
        </aside>
      </div>
    </>
  );
};

export default Navbar;
